import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Trash2, Plus, ArrowLeft } from "lucide-react";
import { useLocation } from "wouter";

interface UtmOverride {
  id: string;
  matchField: "utm_source" | "utm_medium" | "utm_campaign" | "utm_content" | "utm_term";
  matchValue: string;
  metaCampaignId: string | null;
  metaAdSetId: string | null;
  metaAdId: string | null;
  notes: string | null;
  createdAt: string;
}

const FIELD_OPTIONS: Array<{ value: UtmOverride["matchField"]; label: string }> = [
  { value: "utm_campaign", label: "utm_campaign" },
  { value: "utm_source", label: "utm_source" },
  { value: "utm_medium", label: "utm_medium" },
  { value: "utm_content", label: "utm_content" },
  { value: "utm_term", label: "utm_term" },
];

export default function AttributionOverridesPage() {
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  const [draft, setDraft] = useState({
    matchField: "utm_campaign" as UtmOverride["matchField"],
    matchValue: "",
    metaCampaignId: "",
    metaAdSetId: "",
    metaAdId: "",
    notes: "",
  });

  const { data, isLoading } = useQuery<{ overrides: UtmOverride[] }>({
    queryKey: ["utm-overrides"],
    queryFn: () => apiRequest("GET", "/api/tracking/utm-overrides"),
  });

  const createMutation = useMutation({
    mutationFn: (body: typeof draft) => apiRequest("POST", "/api/tracking/utm-overrides", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["utm-overrides"] });
      setDraft({
        matchField: "utm_campaign",
        matchValue: "",
        metaCampaignId: "",
        metaAdSetId: "",
        metaAdId: "",
        notes: "",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/tracking/utm-overrides/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["utm-overrides"] }),
  });

  const overrides = data?.overrides ?? [];

  const submitDisabled =
    !draft.matchValue.trim() ||
    (!draft.metaCampaignId.trim() && !draft.metaAdSetId.trim() && !draft.metaAdId.trim());

  return (
    <div className="min-h-screen bg-background text-foreground p-6 max-w-5xl mx-auto">
      <button
        onClick={() => setLocation("/dashboard")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Wróć do dashboardu
      </button>

      <h1 className="text-2xl font-bold mb-2">Mapowanie atrybucji UTM → Meta</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Jeśli Twoje formularze (Skool, Optinly, Tally itp.) podają tekstowy{" "}
        <code className="px-1 bg-muted rounded">utm_campaign</code> jak{" "}
        <code className="px-1 bg-muted rounded">community-ads</code> zamiast numerycznego ID Meta —
        leady przepadają do "Organic" zamiast pod konkretną reklamę. Tutaj ręcznie mapujesz tekstowe
        wartości UTM do prawdziwych Meta ID, a system automatycznie poprawi atrybucję.
      </p>

      {/* Create form */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Dodaj nowe mapowanie
        </h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Pole UTM</label>
            <select
              value={draft.matchField}
              onChange={(e) =>
                setDraft({ ...draft, matchField: e.target.value as UtmOverride["matchField"] })
              }
              className="w-full h-9 px-2 text-sm rounded border border-border bg-background"
            >
              {FIELD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">
              Wartość (taka jak w utm-ie, case-insensitive)
            </label>
            <input
              type="text"
              value={draft.matchValue}
              onChange={(e) => setDraft({ ...draft, matchValue: e.target.value })}
              placeholder="np. community-ads"
              className="w-full h-9 px-2 text-sm rounded border border-border bg-background"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Meta Campaign ID</label>
            <input
              type="text"
              value={draft.metaCampaignId}
              onChange={(e) => setDraft({ ...draft, metaCampaignId: e.target.value })}
              placeholder="np. 23854xxxxxx"
              className="w-full h-9 px-2 text-sm rounded border border-border bg-background font-mono"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">
              Meta Ad Set ID (opcjonalnie)
            </label>
            <input
              type="text"
              value={draft.metaAdSetId}
              onChange={(e) => setDraft({ ...draft, metaAdSetId: e.target.value })}
              placeholder="opcjonalnie"
              className="w-full h-9 px-2 text-sm rounded border border-border bg-background font-mono"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">
              Meta Ad ID (opcjonalnie)
            </label>
            <input
              type="text"
              value={draft.metaAdId}
              onChange={(e) => setDraft({ ...draft, metaAdId: e.target.value })}
              placeholder="opcjonalnie"
              className="w-full h-9 px-2 text-sm rounded border border-border bg-background font-mono"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">
              Notatka (opcjonalnie)
            </label>
            <input
              type="text"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="np. Skool community form"
              className="w-full h-9 px-2 text-sm rounded border border-border bg-background"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => createMutation.mutate(draft)}
            disabled={submitDisabled || createMutation.isPending}
            className="px-4 py-2 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {createMutation.isPending ? "Zapisuję…" : "Dodaj mapowanie"}
          </button>
        </div>
        {createMutation.isError && (
          <div className="mt-2 text-xs text-red-500">
            {(createMutation.error as Error)?.message}
          </div>
        )}
      </div>

      {/* List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold">Aktywne mapowania ({overrides.length})</h2>
        </div>
        {isLoading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Ładowanie…</div>
        ) : overrides.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Brak mapowań. Dodaj pierwsze powyżej.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Pole</th>
                <th className="px-4 py-2 text-left">Wartość</th>
                <th className="px-4 py-2 text-left">Campaign ID</th>
                <th className="px-4 py-2 text-left">Ad Set ID</th>
                <th className="px-4 py-2 text-left">Ad ID</th>
                <th className="px-4 py-2 text-left">Notatka</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((o) => (
                <tr key={o.id} className="border-t border-border">
                  <td className="px-4 py-2 font-mono text-xs">{o.matchField}</td>
                  <td className="px-4 py-2">{o.matchValue}</td>
                  <td className="px-4 py-2 font-mono text-xs">{o.metaCampaignId || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{o.metaAdSetId || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{o.metaAdId || "—"}</td>
                  <td className="px-4 py-2 text-muted-foreground">{o.notes || "—"}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => {
                        if (confirm(`Usunąć mapowanie ${o.matchField}=${o.matchValue}?`)) {
                          deleteMutation.mutate(o.id);
                        }
                      }}
                      className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition"
                      title="Usuń"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
